import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import type { PublishedItemStatus } from '@tempo-io/roadmap-dto';
import type { ItemStatus } from '@/common/api/types/itemStatus';
import { getItemStatusesQueryOptions } from '@/common/queries/useGetItemStatusesQuery';
import { commonQueryKeys } from '@/common/queryKeys/common';
import { usePublishedContext } from '@/features/views/components/published/PublishedContext';
import { usePublishedSnapshotQuery } from '@/features/views/components/published/usePublishedSnapshotQuery';

const VALID_SCHEDULING_TYPES = new Set(['TO_DO', 'IN_PROGRESS', 'DONE']);

function mapPublishedStatus(s: PublishedItemStatus): ItemStatus {
  return {
    id: s.id,
    name: s.name,
    color: s.color,
    default: false,
    archivedAt: null,
    schedulingType: VALID_SCHEDULING_TYPES.has(s.type)
      ? (s.type as ItemStatus['schedulingType'])
      : 'TO_DO',
    priority: '',
    createdAt: '',
    updatedAt: '',
  };
}

export const useItemStatusesQueryWithPublished = (
  options: Partial<UseQueryOptions<ItemStatus[]>> = {},
) => {
  const publishedContext = usePublishedContext();
  const isPublished = publishedContext !== null;

  const statusesQuery = useQuery({
    ...getItemStatusesQueryOptions(),
    ...options,
    enabled: !isPublished && options.enabled !== false,
  });

  const publishedQuery = usePublishedSnapshotQuery({
    baseKey: commonQueryKeys.itemStatuses.all(),
    getSnapshot: () => publishedContext?.itemStatuses.map(mapPublishedStatus),
  });

  return isPublished ? publishedQuery : statusesQuery;
};
